import React from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';

const notFoundCopy = {
  '/discover': {
    message: "The path ends here, but discovery never does. Return to Discover and explore design, art, architecture, and technology.",
    button: "Back to Discover",
    href: "/discover",
  },
  '/discover/design': {
    message: "This design cannot be found, yet creativity continues. Return to Design and uncover new forms of vision.",
    button: "Back to Design",
    href: "/discover/design",
  },
  '/discover/art': {
    message: "This artwork is out of sight, but expression never fades. Return to Art and encounter creation anew.",
    button: "Back to Art",
    href: "/discover/art",
  },
  '/discover/architecture': {
    message: "This structure cannot stand here, but architecture still frames our future. Return to Architecture.",
    button: "Back to Architecture",
    href: "/discover/architecture",
  },
  '/discover/technology': {
    message: "This page is without signal, but innovation still connects us. Return to Technology and continue exploring.",
    button: "Back to Technology",
    href: "/discover/technology",
  },
  '/connect': {
    message: "Connections sometimes wander, but the rhythm of collaboration remains. Return to Connect.",
    button: "Reconnect",
    href: "/connect",
  },
  '/connect/spaces-of-memory': {
    message: "Some spaces fade, yet builders continue to shape landmarks of memory. Return to Spaces.",
    button: "Back to Spaces",
    href: "/connect/spaces-of-memory",
  },
  '/connect/timeless-shapes': {
    message: "Forms may vanish, but Africa's timeless shapes endure. Return to Shapes.",
    button: "Back to Shapes",
    href: "/connect/timeless-shapes",
  },
  '/connect/living-stories': {
    message: "Not every story can be told, but voices still rise in chorus. Return to Stories.",
    button: "Back to Stories",
    href: "/connect/living-stories",
  },
  '/vision': {
    message: "The future you seek is not here, but DAAT's vision still guides the way. Return to our Vision and see where heritage takes its next step.",
    button: "Back to The Vision",
    href: "/vision",
  },
  '/spaces': {
    message: "This project is not in view, but our spaces remain open for discovery. Return to Project Spaces.",
    button: "Back to Home",
    href: "/",
  },
  '/partner': {
    message: "This arch to partnership is missing, but collaborations are always open. Return to Collaborate.",
    button: "Back to Home",
    href: "/",
  },
  '/mavericks': {
    message: "This perspective may be momentarily unseen, but leadership lives on. Return to Mavericks and Leaders to explore bold voices shaping the world of design.",
    button: "Return to Mavericks and Leaders",
    href: "/mavericks",
  },
  '/jali': {
    message: "This verse may be missing, but the narrative continues. Return to JALI Stories to rediscover the voices shaping how story becomes memory, and memory becomes movement.",
    button: "Return to JALI Stories",
    href: "/jali",
  },
  '/jali-stories': {
    message: "This story is hidden, not lost. Return to JALI to listen again.",
    button: "Return to JALI",
    href: "/jali-stories",
  },
  '/guardians': {
    message: "This Guardian cannot be found, but their vision still shapes our world. Return to Guardians to explore the leaders who continue to guide design and cultural memory echoed through time.",
    button: "Return to Guardians",
    href: "/guardians",
  },
  '/experts': {
    message: "This expert may be beyond the frame, but their insight lives on. Return to Experts to explore more of their perspectives rooted in African design systems.",
    button: "Return to Experts",
    href: "/experts",
  },
  '/articles': {
    message: "This article is missing, but the stories of our thought leaders remain. Return to Articles to meet the visionaries shaping heritage and innovation.",
    button: "Back to Home",
    href: "/",
  },
};

const getRouteMatch = (pathname) => {
  if (notFoundCopy[pathname]) return notFoundCopy[pathname];
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 1) {
    const parentPath = '/' + segments[0];
    if (notFoundCopy[parentPath]) return notFoundCopy[parentPath];
  }
  return null;
};

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const EASE = [0.76, 0, 0.24, 1];

  const match = getRouteMatch(location.pathname);

  const message = match?.message || "The page you are looking for does not exist. But DAAT's world of design, art, architecture, and technology is still here for you.";
  const button  = match?.button  || "Return Home";
  const href    = match?.href    || "/";

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center px-6 relative overflow-hidden"
      style={{ background: '#050505' }}
    >
      <motion.div
        className="absolute w-[600px] h-[400px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(164,127,45,0.12) 0%, transparent 70%)', filter: 'blur(80px)', top: '10%', left: '-10%' }}
        animate={{ x: [0, 40, 0], y: [0, 20, 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute w-[400px] h-[300px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(139,94,60,0.1) 0%, transparent 70%)', filter: 'blur(60px)', bottom: '5%', right: '-5%' }}
        animate={{ x: [0, -30, 0], y: [0, -20, 0] }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: EASE }}
        className="flex flex-col items-center text-center max-w-2xl relative z-10"
      >
        <motion.div
          className="relative flex items-center justify-center mb-10"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: EASE }}
        >
          <motion.div
            className="absolute w-40 h-40 rounded-full"
            style={{ background: 'conic-gradient(from 0deg, #A47F2D, #E8C97A, #8B5E3C, #C2944A, #A47F2D)', padding: '1.5px' }}
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          >
            <div className="w-full h-full rounded-full bg-[#050505]" />
          </motion.div>
          <motion.div
            className="absolute w-28 h-28 rounded-full"
            style={{ border: '1px dashed rgba(228,201,122,0.4)' }}
            animate={{ rotate: -360 }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
          />
          <motion.span
            className="font-bold relative z-10 select-none"
            style={{
              fontFamily: "'Ojuju', sans-serif",
              fontSize: 'clamp(40px, 6vw, 72px)',
              background: 'linear-gradient(135deg, #E8C97A 0%, #A47F2D 50%, #C2944A 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
            animate={{
              filter: [
                'drop-shadow(0 0 6px rgba(164,127,45,0.4))',
                'drop-shadow(0 0 18px rgba(232,201,122,0.8))',
                'drop-shadow(0 0 6px rgba(164,127,45,0.4))'
              ]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            404
          </motion.span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="flex items-center gap-4 w-64 mb-8"
        >
          <div className="h-[0.5px] flex-1" style={{ background: 'linear-gradient(to right, transparent, #A47F2D)' }} />
          <span style={{ color: '#E8C97A', fontSize: 10 }}>✦</span>
          <div className="h-[0.5px] flex-1" style={{ background: 'linear-gradient(to left, transparent, #A47F2D)' }} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-white/70 leading-relaxed mb-10"
          style={{ fontFamily: "'Poppins', sans-serif", fontSize: 'clamp(13px, 1.5vw, 17px)', fontWeight: 300 }}
        >
          {message}
        </motion.p>

        <motion.button
          onClick={() => navigate(href)}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
          style={{
            background: 'linear-gradient(135deg, #C9A063, #A47F2D)',
            border: 'none',
            borderRadius: 9999,
            padding: 'clamp(10px, 1.5vw, 14px) clamp(28px, 4vw, 48px)',
            color: '#000',
            fontFamily: "'Ojuju', sans-serif",
            fontSize: 'clamp(11px, 1.2vw, 14px)',
            fontWeight: 'bold',
            letterSpacing: '0.08em',
            cursor: 'pointer',
          }}
        >
          {button}
        </motion.button>

        {href !== '/' && (
            <motion.button
                onClick={() => navigate('/')}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="mt-4 text-white/30 hover:text-white/60 transition-colors"
                style={{ fontFamily: "'Poppins', sans-serif", fontSize: 11, background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.15em' }}
            >
                RETURN HOME
            </motion.button>
            )}
      </motion.div>
    </div>
  );
};

export default NotFound;